import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearch } from '@tanstack/react-router'
import { CalendarClock, ChevronRight, Copy, Download, FileText, Loader2, Plus, Trash2, Upload } from 'lucide-react'

import { api } from '@/api/client'
import { confirm } from '@/stores/confirm'
import { useTabField } from '@/stores/tabState'
import { useAuth } from '@/stores/auth'
import { useCurrentCompany } from '@/lib/useCompany'
import { useFileDownload } from '@/lib/useFileDownload'
import type { Item } from '@/types/models'
import { useToast } from '@/stores/toast'
import { Modal } from './Modal'
import { SearchBox } from './SearchBox'
import { PartnerPicker } from './PartnerPicker'
import { ItemCombo } from './ItemCombo'
import { DateRangeBar } from './DateRangeBar'
import { RefreshButton } from './RefreshButton'
import { cn, formatNum, isFractionalCountUnit, isLargeQty } from '@/lib/utils'
import { FIELDS, decideRowNav, type FieldName } from '@/lib/lineNav'
import { sortOrders, nextSort, type ListSortKey } from '@/lib/orderSort'

// 출고/입고 공통 라인·전표 shape (DeliveryLine ≡ ReceivingLine, DeliveryOrder ≡ ReceivingOrder)
interface OrderLine {
  id: number
  item: number
  item_code: string
  item_name: string
  spec: string
  unit: string
  qty: string
  unit_price: string
  supply_amount: string
  vat_amount: string
  total: string
  manual_amount?: boolean
  note: string
  note2?: string
  is_prepaid?: boolean
  subdivide?: number
  sale_type?: SaleType
}

interface OrderRecord {
  id: number
  order_no: string
  order_date: string
  partner: number
  partner_name: string
  vat_type: 'vat' | 'none'
  tax_type: 'taxable' | 'exempt' | 'zero'
  note: string
  is_carryover?: boolean
  subtotal: string
  vat_total: string
  total: string
  lines?: OrderLine[]        // slim 목록에선 생략 — 펼침/수정 시 단건 조회로 로드
  lines_count?: number       // slim 목록의 라인 건수
}

// 전표 목록 정렬 로직(ListSortKey 등)은 @/lib/orderSort 로 분리(단위 테스트).

// 출고유형(줄 단위) — 매출 외는 금액 0원 고정, 구매장·라벨·협력업체 발주서에만 포함(고객 요청 2026-11-01).
type SaleType = 'sale' | 'postpaid' | 'exchange' | 'preorder' | 'sponsor'
const SALE_TYPES: [SaleType, string][] = [
  ['sale', '매출'], ['postpaid', '후납'], ['exchange', '교환'], ['preorder', '선주문'], ['sponsor', '찬조'],
]

interface LineForm {
  id?: number           // 기존 저장된 라인의 DeliveryLine id (날짜 분리 선택용). 신규 라인은 없음.
  item: number | null
  item_code: string
  item_name: string
  spec: string
  unit: string
  qty: number | string   // 편집 중 소수 입력("0.", "0.02")을 문자열로 보존, 저장 시 서버가 Decimal 파싱
  unit_price: number
  // 공급가액·부가세 수기지정. manual_amount=true 면 supply/vat 를 자동계산하지
  // 않고 아래 값을 그대로 저장(면세 전표에 과세 품목이 섞인 경우 등).
  manual_amount: boolean
  supply_amount?: number
  vat_amount?: number
  note: string          // 비고1 — 업장/거래처 관련 (구매장에서 거래처명 옆 병기)
  note2: string         // 비고2 — 품목 코멘트 (구매장에서 품목명 옆 병기)
  is_prepaid: boolean   // 선납 — 구매장 엑셀에서 제외 (출고 전용)
  subdivide: number     // 소분 — 구매장 엑셀에서 N줄 균등분할 (출고 전용, 0=미사용)
  sale_type: SaleType   // 출고유형 (출고 전용)
  // 직전단가 (오타 방지용, 매출처 flag on 일 때만 채워짐).
  previous_unit_price?: number
  previous_order_date?: string
  previous_unit?: string
  // 직전 출고(판매)단가(상위거래처 그룹 기준, 직전 날짜) — 판매가 참고. Delivery 전용.
  // conflict=true 면 같은 날 그룹 내 업장 단가가 갈림(⚠), detail 에 업장별 단가.
  last_out_price?: number
  last_out_date?: string
  last_out_conflict?: boolean
  last_out_detail?: string
  // 거래처별 이 품목 출고수량 참고 (해당월/직전월). Delivery 전용.
  curr_month_qty?: number
  prev_month_qty?: number
  curr_month_label?: string
  prev_month_label?: string
}

// 전표별 펼침 상세 참고열 — 이번달/저번달 출고수량 누계 (month-qtys API 응답).
interface MonthQtyData {
  curr_month_label: string
  prev_month_label: string
  lines: Record<number, {
    curr_month_qty: number; prev_month_qty: number
    last_out_price: number | null; last_out_date: string | null
    last_out_conflict: boolean; last_out_detail: string
  }>
}

// 수정 창 열 때 쓰는 같은 응답 — 직전 청구 단가(거래처 '직전단가 표시' 켜진 경우)까지.
interface MonthQtyBatch extends Omit<MonthQtyData, 'lines'> {
  show_previous: boolean
  lines: Record<number, MonthQtyData['lines'][number] & {
    previous_unit_price: number | string | null
    previous_order_date: string | null
    previous_unit: string | null
  }>
}

interface OrderForm {
  id?: number
  order_date: string
  partner: number | null
  vat_type: 'vat' | 'none'
  tax_type: 'taxable' | 'exempt' | 'zero'
  note: string
  is_carryover?: boolean
  lines: LineForm[]
}

const blankOrder = (): OrderForm => ({
  order_date: '',   // 기본 빈 날짜 — 무의식 오등록 방지(사용자가 반드시 지정, required)
  partner: null, vat_type: 'none', tax_type: 'exempt', note: '', is_carryover: false, lines: [],
})
const blankLine = (): LineForm => ({
  item: null, item_code: '', item_name: '',
  spec: '', unit: '', qty: 0, unit_price: 0, note: '', note2: '',
  manual_amount: false, is_prepaid: false, subdivide: 0, sale_type: 'sale',
})

// 라인 내 필드 순서(FIELDS)·방향키 결정 로직은 @/lib/lineNav 로 분리(단위 테스트).

// 상단 폼 필드 방향키 이동 순서 (← → 선형).
const FORM_FIELDS = ['order_date', 'partner', 'vat_type', 'tax_type', 'note'] as const
type FormField = typeof FORM_FIELDS[number]

// ↑ ↓ 는 그리드 시각적 배치 기반.
// [출고일자] [거래처       ] [VAT여부]
// [과세구분] [적요                  ]
const DOWN_MAP: Record<FormField, FormField | 'add-line'> = {
  order_date: 'tax_type',
  partner:    'note',
  vat_type:   'note',
  tax_type:   'add-line',
  note:       'add-line',
}
const UP_MAP: Record<FormField, FormField | null> = {
  order_date: null,
  partner:    null,
  vat_type:   null,
  tax_type:   'order_date',
  note:       'partner',
}

export interface OrderCrudConfig {
  // 라벨 / 문구
  entity: string           // '출고전표' | '입고전표'
  entityDate: string       // '출고일자' | '입고일자'
  entityNo: string         // '출고번호' | '입고번호'
  partnerLabel: string     // '거래처' | '매입처'
  specColLabel: string     // '규격(원산지)' | '규격'
  linesTitle: string       // '출고 라인' | '입고 라인'
  searchPlaceholder: string
  emptyText: string
  excelFilePrefix: string  // '출고처리' | '입고처리'

  // 데이터
  apiBase: string          // '/api/delivery' | '/api/receiving'
  exportUrl: string        // '/api/delivery/export' | '/api/receiving/export'
  suggestUrl: string       // '/api/delivery/suggest-price' | ...
  queryKey: string         // 'deliveries' | 'receivings'

  // 파트너/품목 처리
  partnerBizClass?: 'customer' | 'vendor' | 'both' | ''
  unitField: 'unit_in' | 'unit_out'

  // 스타일
  totalColorClass: string  // 'text-blue-700' | 'text-emerald-700'

  // Delivery 전용: 체크박스 + 거래명세서 인쇄 버튼
  invoicePrint?: boolean
  // Delivery 전용: 비고/선납/소분 열 (구매장 엑셀 반영용)
  purchaseFields?: boolean
  // Delivery 전용: 등록화면에 '이월' 체크(매출관리 일일매출에서 다음달 이월로 반영)
  carryoverFlag?: boolean
  // Delivery 전용: 엑셀(더존 출고 양식) 업로드로 전표 일괄 등록 버튼
  excelImport?: boolean
  // Delivery 전용: 수정 시 선택 라인만 다른 출고일자로 분리 (split-lines API)
  lineDateSplit?: boolean
  // Delivery 전용: 전표별 펼침 상세에 이번달/저번달 출고수량 누계 참고열 (month-qtys API)
  monthQtyRef?: boolean
  // Delivery 전용: 목록을 라인 없이(slim) 받아 대량 기간 페이로드 축소. 라인은
  // 펼침/수정 시 해당 전표만 단건 조회. (백엔드 ?slim=1 지원 필요)
  slimList?: boolean
}

export function OrderCrudTab({ config: c }: { config: OrderCrudConfig }) {
  const qc = useQueryClient()
  const push = useToast((s) => s.push)
  const { token } = useAuth()
  const company = useCurrentCompany()
  const { downloading, download } = useFileDownload()

  // ?order=&from=&to= 딥링크
  const qp = useSearch({ strict: false }) as { order?: string | number; from?: string; to?: string }
  // 기간·검색은 탭을 오가도 유지(스코프 키=queryKey → 출고/입고 분리). 선택·편집은 로컬.
  // 기본 빈 날짜 — 사용자가 기간을 직접 지정해야 조회(무의식 오조회 방지). 세션 내 유지.
  const [dateFrom, setDateFrom] = useTabField<string>(c.queryKey, 'dateFrom', qp.from || '')
  const [dateTo, setDateTo] = useTabField<string>(c.queryKey, 'dateTo', qp.to || '')
  const [search, setSearch] = useTabField(c.queryKey, 'search', '')
  const [edit, setEdit] = useState<OrderForm | null>(null)
  const [expanded, setExpanded] = useState<number | null>(qp.order ? Number(qp.order) : null)
  // 전표별 펼침 상세 — 이번달/저번달 출고수량 누계. 펼칠 때 전표별로 지연 조회.
  const [monthQtys, setMonthQtys] = useState<Record<number, MonthQtyData>>({})

  const rowRefs = useRef<Record<number, HTMLTableRowElement | null>>({})
  const [highlight, setHighlight] = useState<number | null>(null)

  // 목록 헤더(노란색, sticky top-0)의 실제 높이 — 좁은 폭에선 '품목수' 등이
  // 2줄로 줄바꿈돼 높이가 달라진다. 상세 라인의 연두색 헤더를 이 높이만큼
  // 아래에 sticky 고정해야 노란 헤더에 가려지지 않는다(폭 변화에 반응).
  const listHeadRef = useRef<HTMLTableSectionElement>(null)
  const [listHeadH, setListHeadH] = useState(40)
  useEffect(() => {
    const el = listHeadRef.current
    if (!el) return
    const update = () => setListHeadH(el.getBoundingClientRect().height)
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // 명세서 인쇄용 선택 (invoicePrint 옵션 있는 탭에서만 사용)
  const [selected, setSelected] = useState<Set<number>>(new Set())
  // 날짜이동 직후 이동한 전표 하이라이트(다음 선택 동작 전까지 유지).
  const [movedIds, setMovedIds] = useState<Set<number>>(new Set())
  // Shift+클릭 범위선택용 앵커(직전 클릭한 행 인덱스)
  const [lastIdx, setLastIdx] = useState<number | null>(null)
  // 안전장치: 조회 기간(날짜)을 바꾸면 선택을 초기화한다. 여러 날짜를 오가며
  // 선택이 누적돼 엉뚱한 날짜 전표가 날짜이동에 딸려가는 사고 방지.
  useEffect(() => { setSelected(new Set()); setLastIdx(null) }, [dateFrom, dateTo])
  // 선택 전표 날짜이동 모달
  const [moveOpen, setMoveOpen] = useState(false)
  const [moveDate, setMoveDate] = useState('')
  // 선택 전표 날짜복사 모달 (다른 날짜로만 복제)
  const [copyOpen, setCopyOpen] = useState(false)
  const [copyDate, setCopyDate] = useState('')

  const { data: orders = [], refetch, isFetching } = useQuery({
    queryKey: [c.queryKey, dateFrom, dateTo, search],
    queryFn: () => {
      const p = new URLSearchParams({ from: dateFrom, to: dateTo })
      if (search) p.set('q', search)
      if (c.slimList) p.set('slim', '1')   // 라인 없이 받아 페이로드 축소
      return api<OrderRecord[]>(`${c.apiBase}?${p}`)
    },
    enabled: !!dateFrom && !!dateTo,   // 기간 지정 전엔 조회 안 함
  })

  // slim 목록의 라인 지연로딩 캐시. 목록이 갱신되면(기간/검색 변경·저장·삭제 등)
  // 비워 항상 최신 라인을 다시 받게 한다.
  const [linesById, setLinesById] = useState<Record<number, OrderLine[]>>({})
  useEffect(() => { setLinesById({}) }, [orders])
  // 전표의 라인: 목록에 있으면 그대로(입고 등 non-slim), 없으면 캐시.
  const linesOf = (o: OrderRecord): OrderLine[] | undefined => o.lines ?? linesById[o.id]
  // 라인 확보(없으면 단건 조회) — 펼침/수정에서 사용.
  async function ensureLines(o: OrderRecord): Promise<OrderLine[]> {
    const cur = linesOf(o)
    if (cur) return cur
    const full = await api<OrderRecord>(`${c.apiBase}/${o.id}`)
    const ls = full.lines ?? []
    setLinesById((m) => ({ ...m, [o.id]: ls }))
    return ls
  }

  // 수정 창은 늘 서버에서 새로 받아 연다 — 저장 직후 목록 새로고침이 끝나기 전에 다시 열면 옛 내용
  // (방금 쓴 비고 없음)이 열리고, 그대로 저장하면 비고가 지워졌음(2026-10-29).
  async function openFresh(o: OrderRecord): Promise<OrderRecord | null> {
    try {
      const full = await api<OrderRecord>(`${c.apiBase}/${o.id}`)
      setLinesById((m) => ({ ...m, [o.id]: full.lines ?? [] }))
      return full
    } catch (e) {
      push(`전표를 불러오지 못했습니다: ${e instanceof Error ? e.message : String(e)}`, 'error', 5000)
      return null
    }
  }

  // ── 전표 목록 열 헤더 클릭 정렬 ──
  const [listSort, setListSort] = useState<{ key: ListSortKey; dir: 'asc' | 'desc' } | null>(null)
  const sortedOrders = useMemo(() => sortOrders(orders, listSort), [orders, listSort])
  function onListSort(key: ListSortKey) {
    setListSort((cur) => nextSort(cur, key))
    setLastIdx(null)   // 정렬이 바뀌면 shift-범위선택 앵커 초기화(인덱스 의미가 달라짐)
  }
  const listSortMark = (k: ListSortKey) => (listSort?.key === k ? (listSort.dir === 'asc' ? '▲' : '▼') : '↕')
  function listSortTh(k: ListSortKey, label: React.ReactNode, thClass: string) {
    return (
      <th className={thClass}>
        <button type="button" onClick={() => onListSort(k)}
                title="클릭하면 이 열 기준으로 정렬(다시 클릭 시 역순)"
                className="inline-flex items-center gap-0.5 hover:text-slate-700">
          <span>{label}</span>
          <span className={cn('text-[9px] leading-none', listSort?.key === k ? 'text-slate-600' : 'text-slate-300')}>
            {listSortMark(k)}
          </span>
        </button>
      </th>
    )
  }

  const allSelected = orders.length > 0 && orders.every((o) => selected.has(o.id))
  const anySelected = selected.size > 0
  function toggleOne(id: number, idx: number, shift: boolean) {
    setMovedIds((m) => (m.size ? new Set() : m))   // 새 선택 시작 → 이동 하이라이트 해제
    // Shift+클릭: 직전 클릭한 행 ~ 현재 행 범위를 일괄로 (클릭한 행의 새 상태에 맞춰) 토글
    if (shift && lastIdx !== null && lastIdx !== idx) {
      const [a, b] = idx < lastIdx ? [idx, lastIdx] : [lastIdx, idx]
      const rangeIds = sortedOrders.slice(a, b + 1).map((o) => o.id)   // 표시(정렬) 순서 기준
      const check = !selected.has(id)
      setSelected((cur) => {
        const next = new Set(cur)
        for (const rid of rangeIds) { if (check) next.add(rid); else next.delete(rid) }
        return next
      })
      setLastIdx(idx)
      return
    }
    setSelected((cur) => {
      const next = new Set(cur)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
    setLastIdx(idx)
  }
  function toggleAll() {
    setMovedIds((m) => (m.size ? new Set() : m))   // 새 선택 시작 → 이동 하이라이트 해제
    if (allSelected) setSelected(new Set())
    else setSelected(new Set(orders.map((o) => o.id)))
    setLastIdx(null)
  }
  function openInvoicePrint() {
    // 출고전표 건별로 1장 — 선택한 각 전표(order_no)가 개별 명세서가 된다.
    // (같은 거래처·날짜라도 합치지 않음: 반야처럼 PO별 분리 등록된 전표를 각각 출력)
    // 단가·금액 숨김은 거래처 관리 명세서 설정으로 제어(전표별 토글 없음).
    const tokens: string[] = []
    for (const o of orders) {
      if (!selected.has(o.id)) continue
      tokens.push(`${o.partner}|${o.order_date}|${o.order_no}`)
    }
    if (!tokens.length) { push('전표를 선택하세요', 'warn'); return }
    // 인쇄 창도 회사를 알아야 하므로 URL 에 ?company 포함 (창별 회사).
    const qp = new URLSearchParams({ buckets: tokens.join(','), autoprint: '1' })
    if (company) qp.set('company', String(company.id))
    window.open(`/invoice-print?${qp}`, '_blank', 'noopener,noreferrer')
  }

  async function downloadExcel() {
    if (!orders.length) { push(`내보낼 ${c.entity}가 없습니다`, 'warn'); return }
    const params = new URLSearchParams({ from: dateFrom, to: dateTo })
    if (search) params.set('q', search)
    await download(`${c.exportUrl}?${params}`, `${c.excelFilePrefix}_${dateFrom}_${dateTo}.xlsx`)
  }

  useEffect(() => {
    if (!expanded || !orders.length) return
    const el = rowRefs.current[expanded]
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      setHighlight(expanded)
      const t = setTimeout(() => setHighlight(null), 2000)
      return () => clearTimeout(t)
    }
  }, [expanded, orders])

  // 날짜이동 직후: 목록이 갱신되면 첫 이동 전표로 스크롤(맨 아래로 밀려도 보이게) +
  // 그 행을 잠깐 깜빡임. 깜빡임 끝나면 표시 해제(영구 표시 없음).
  useEffect(() => {
    if (!movedIds.size || !orders.length) return
    const first = orders.find((o) => movedIds.has(o.id))
    const el = first ? rowRefs.current[first.id] : null
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    const t = setTimeout(() => setMovedIds(new Set()), 4000)   // 깜빡임(약 3.5s) 후 해제
    return () => clearTimeout(t)
  }, [movedIds, orders])

  // 전표를 펼치면(monthQtyRef 켜진 출고 탭) 그 전표의 월수량 누계를 한 번만 지연 조회.
  useEffect(() => {
    if (!c.monthQtyRef || !expanded || monthQtys[expanded]) return
    let cancelled = false
    api<MonthQtyData>(`${c.apiBase}/${expanded}/month-qtys`)
      .then((d) => { if (!cancelled) setMonthQtys((m) => ({ ...m, [expanded]: d })) })
      .catch(() => { /* 참고 정보라 실패해도 조용히 무시 */ })
    return () => { cancelled = true }
  }, [expanded, c.monthQtyRef, c.apiBase, monthQtys])

  // 더블클릭·Ctrl+Enter 연타로 저장 요청이 두 번 가지 않게 — 렌더 전에도 막히는 즉시 잠금.
  const savingRef = useRef(false)
  const save = useMutation({
    onSettled: () => { savingRef.current = false },
    mutationFn: (f: OrderForm) => {
      const payload = {
        order_date: f.order_date, partner: f.partner,
        vat_type: f.vat_type, tax_type: f.tax_type, note: f.note,
        ...(c.carryoverFlag ? { is_carryover: !!f.is_carryover } : {}),
        lines: f.lines.map((l) => ({
          item: l.item, spec: l.spec, unit: l.unit,
          qty: Number(l.qty) || 0, unit_price: l.unit_price, note: l.note, note2: l.note2,
          is_prepaid: l.is_prepaid, subdivide: l.subdivide,
          ...(c.purchaseFields ? { sale_type: l.sale_type } : {}),
          manual_amount: l.manual_amount,
          ...(l.manual_amount
            ? { supply_amount: lineSupply(l), vat_amount: lineVat(l) }
            : {}),
        })),
      }
      return f.id
        ? api(`${c.apiBase}/${f.id}`, { method: 'PATCH', body: payload })
        : api(c.apiBase, { method: 'POST', body: payload })
    },
    onSuccess: (_data, f) => {
      qc.invalidateQueries({ queryKey: [c.queryKey] })
      // 수정 시 라인 id가 재생성되므로, 라인 id로 매칭되는 month-qtys 캐시(직전출고단가·
      // 월누계)를 비워 새 라인 id로 재조회되게 한다(안 그러면 직전출고단가가 사라짐).
      if (f.id != null) setMonthQtys((m) => { const n = { ...m }; delete n[f.id!]; return n })
      setEdit(null); push('저장됨', 'success')
    },
    onError: (e) => push(`저장 실패: ${e.message}`, 'error', 5000),
  })

  const remove = useMutation({
    mutationFn: (id: number) => api(`${c.apiBase}/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [c.queryKey] }),
  })

  // ── 선택 전표 일괄삭제 / 날짜이동 (invoicePrint 탭 전용) ──
  const bulkDelete = useMutation({
    mutationFn: (ids: number[]) =>
      api<{ deleted: number }>(`${c.apiBase}/bulk-delete`, { method: 'POST', body: { ids } }),
    onSuccess: (data) => {
      push(`${data.deleted}건 삭제 완료`, 'success')
      setSelected(new Set()); setLastIdx(null)
      qc.invalidateQueries({ queryKey: [c.queryKey] })
    },
    onError: (e: any) => push(e?.message || '삭제 실패', 'error'),
  })
  const bulkMove = useMutation({
    mutationFn: (vars: { ids: number[]; newDate: string }) =>
      api<{ moved: number; new_date: string }>(`${c.apiBase}/bulk-move-date`,
        { method: 'POST', body: { ids: vars.ids, new_date: vars.newDate } }),
    onSuccess: (data, vars) => {
      push(`${data.moved}건을 ${data.new_date} 로 이동 완료`, 'success')
      setSelected(new Set()); setLastIdx(null); setMoveOpen(false); setMoveDate('')
      setMovedIds(new Set(vars.ids))   // 이동한 전표 하이라이트(다음 선택 전까지)
      qc.invalidateQueries({ queryKey: [c.queryKey] })
    },
    onError: (e: any) => push(e?.message || '날짜 이동 실패', 'error'),
  })
  const bulkCopy = useMutation({
    mutationFn: (vars: { ids: number[]; newDate: string }) =>
      api<{ copied: number; new_date: string; created_ids: number[]; skipped_same_date: number }>(
        `${c.apiBase}/bulk-copy-date`,
        { method: 'POST', body: { ids: vars.ids, new_date: vars.newDate } }),
    onSuccess: (data) => {
      const extra = data.skipped_same_date ? ` (같은 날짜 ${data.skipped_same_date}건 제외)` : ''
      if (data.copied) push(`${data.copied}건을 ${data.new_date} 로 복사 완료${extra}`, 'success')
      else push(`복사된 전표가 없습니다${extra}`, 'warn')
      setSelected(new Set()); setLastIdx(null); setCopyOpen(false); setCopyDate('')
      setMovedIds(new Set(data.created_ids))   // 새로 만든 전표 하이라이트
      qc.invalidateQueries({ queryKey: [c.queryKey] })
    },
    onError: (e: any) => push(e?.message || '날짜 복사 실패', 'error'),
  })
  async function doBulkDelete() {
    const ids = Array.from(selected)
    if (!ids.length) return
    if (!(await confirm(
      `선택한 ${ids.length}건의 ${c.entity}를 삭제할까요? 되돌릴 수 없습니다.`,
      { danger: true },
    ))) return
    bulkDelete.mutate(ids)
  }
  async function doBulkMove() {
    const ids = Array.from(selected)
    if (!ids.length) return
    if (!moveDate) { push(`옮길 ${c.entityDate}를 선택하세요`, 'warn'); return }
    // 안전장치: 선택 전표가 여러 날짜에 걸쳐 있으면 이동 전에 경고(날짜별 건수).
    const byDate: Record<string, number> = {}
    for (const o of orders) if (selected.has(o.id)) byDate[o.order_date] = (byDate[o.order_date] || 0) + 1
    const srcDates = Object.keys(byDate).sort()
    if (srcDates.length > 1) {
      const summary = srcDates.map((d) => `· ${d} ${byDate[d]}건`).join('\n')
      if (!(await confirm(
        `선택한 전표에 여러 날짜가 섞여 있습니다:\n${summary}\n\n정말 전부 ${moveDate}로 이동할까요?`,
        { danger: true },
      ))) return
    }
    bulkMove.mutate({ ids, newDate: moveDate })
  }
  function doBulkCopy() {
    const ids = Array.from(selected)
    if (!ids.length) return
    if (!copyDate) { push(`복사할 ${c.entityDate}를 선택하세요`, 'warn'); return }
    // 다른 날짜로만 — 선택 전표가 모두 그 날짜면 막는다.
    if (orders.filter((o) => ids.includes(o.id)).every((o) => o.order_date === copyDate)) {
      push('원본과 다른 날짜를 선택하세요', 'warn'); return
    }
    bulkCopy.mutate({ ids, newDate: copyDate })
  }

  // ── 선택 라인 날짜 분리 (Delivery 전용) ──
  const [splitSel, setSplitSel] = useState<Set<number>>(new Set())
  const [splitDate, setSplitDate] = useState('')
  const splitLines = useMutation({
    mutationFn: (vars: { orderId: number; lineIds: number[]; newDate: string }) =>
      api<{ moved: number; target_order_no: string; target_created: boolean; source_deleted: boolean }>(
        `${c.apiBase}/${vars.orderId}/split-lines`,
        { method: 'POST', body: { line_ids: vars.lineIds, new_date: vars.newDate } },
      ),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: [c.queryKey] })
      push(
        `${res.moved}건 → ${res.target_order_no} (새 전표로 분리)`,
        'success', 6000,
      )
      setEdit(null)
    },
    onError: (e) => push(`날짜 분리 실패: ${e.message}`, 'error', 6000),
  })

  // ── 엑셀(더존 출고 양식) 업로드로 전표 일괄 등록 (출고·입고) ──
  const excelInputRef = useRef<HTMLInputElement>(null)
  const importExcel = useMutation({
    mutationFn: async (file: File) => {
      const send = async (allowDup: boolean) => {
        const fd = new FormData()
        fd.append('file', file)
        const res = await fetch(`${c.apiBase}/import-excel${allowDup ? '?allow_dup=1' : ''}`, {
          method: 'POST',
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(company ? { 'X-Company-Id': String(company.id) } : {}),
          },
          body: fd,
        })
        return { res, data: await res.json().catch(() => ({})) }
      }
      let { res, data } = await send(false)
      // 입고 월 매입 합계 양식: 이미 같은 날짜·거래처·금액이 있으면 409 → 확인 후 다시
      if (res.status === 409) {
        const list = (data.duplicates ?? []).slice(0, 10).join('\n')
        const ok = await confirm(`${data.detail}\n${list}${(data.duplicates?.length ?? 0) > 10 ? '\n…' : ''}\n\n그래도 모두 등록할까요?`)
        if (!ok) return null
        ;({ res, data } = await send(true))
      }
      if (!res.ok) {
        const errs = Array.isArray(data.errors) ? data.errors.slice(0, 15).join('\n') : ''
        throw new Error([data.detail, errs].filter(Boolean).join('\n') || `HTTP ${res.status}`)
      }
      return data as { created_count: number; total?: string; skipped?: string[] }
    },
    onSuccess: (data) => {
      if (!data) return
      qc.invalidateQueries({ queryKey: [c.queryKey] })
      const sk = data.skipped ?? []
      push(`엑셀 등록 완료 — ${c.entity} ${data.created_count}건 생성`
        + (data.total ? `, ${formatNum(Number(data.total))}원` : '')
        + (sk.length ? `\n금액 없음 ${sk.length}줄 건너뜀:\n${sk.slice(0, 10).join('\n')}` : ''),
        'success', sk.length ? 15000 : 6000)
    },
    onError: (e) => push(`엑셀 등록 실패\n${e instanceof Error ? e.message : String(e)}`, 'error', 10000),
  })
  function onExcelPicked(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = ''   // 같은 파일 재선택 허용
    if (file) importExcel.mutate(file)
  }

  async function applyDateSplit() {
    if (!edit?.id) return
    const ids = [...splitSel]
    if (!ids.length) { push('이동할 품목을 선택하세요', 'warn'); return }
    if (!splitDate) { push(`옮길 ${c.entityDate}를 선택하세요`, 'warn'); return }
    if (splitDate === edit.order_date) { push(`기존 ${c.entityDate}와 같습니다`, 'warn'); return }
    const allSelected = ids.length === edit.lines.filter((l) => l.id).length
    const msg = allSelected
      ? `전체 ${ids.length}개 품목을 ${splitDate} 로 옮깁니다. 이 전표는 비게 되어 삭제되고, 그 날짜에 새 전표로 분리됩니다. 진행할까요?`
      : `선택한 ${ids.length}개 품목을 ${splitDate} 로 새 전표로 분리할까요?`
    if (!(await confirm(msg))) return
    splitLines.mutate({ orderId: edit.id, lineIds: ids, newDate: splitDate })
  }
  // 모달 열림/전표 전환 시 선택·날짜 초기화
  useEffect(() => { setSplitSel(new Set()); setSplitDate('') }, [edit?.id])

  async function suggestPriceApi(item: number, date?: string) {
    if (!edit?.partner) return null
    const d = date ?? edit.order_date
    return api<{
      unit_price: number | string
      source?: string
      previous_unit_price?: number | string
      previous_order_date?: string
      previous_unit?: string
      show_previous?: boolean
      curr_month_qty?: number | string
      prev_month_qty?: number | string
      curr_month_label?: string
      prev_month_label?: string
      last_out_price?: number | string
      last_out_date?: string
      last_out_conflict?: boolean
      last_out_detail?: string
    }>(`${c.suggestUrl}?partner=${edit.partner}&item=${item}&date=${d}`)
      .catch(() => null)
  }

  // 거래처별 이 품목 출고수량(해당월/직전월) + 직전출고단가 → LineForm 패치. (항상 반영)
  function monthQtyPatch(r: {
    curr_month_qty?: number | string
    prev_month_qty?: number | string
    curr_month_label?: string
    prev_month_label?: string
    last_out_price?: number | string
    last_out_date?: string
    last_out_conflict?: boolean
    last_out_detail?: string
  } | null): Partial<LineForm> {
    if (!r) return {}
    return {
      curr_month_qty: r.curr_month_qty != null ? Number(r.curr_month_qty) : undefined,
      prev_month_qty: r.prev_month_qty != null ? Number(r.prev_month_qty) : undefined,
      curr_month_label: r.curr_month_label,
      prev_month_label: r.prev_month_label,
      last_out_price: r.last_out_price != null ? Number(r.last_out_price) : undefined,
      last_out_date: r.last_out_date,
      last_out_conflict: r.last_out_conflict,
      last_out_detail: r.last_out_detail,
    }
  }

  function previousPatch(r: {
    previous_unit_price?: number | string
    previous_order_date?: string
    previous_unit?: string
    show_previous?: boolean
  } | null): Partial<LineForm> {
    if (r?.show_previous && r.previous_unit_price != null) {
      return {
        previous_unit_price: Number(r.previous_unit_price),
        previous_order_date: r.previous_order_date,
        previous_unit: r.previous_unit,
      }
    }
    return {
      previous_unit_price: undefined,
      previous_order_date: undefined,
      previous_unit: undefined,
    }
  }

  async function fillPrice(idx: number, line: LineForm) {
    if (!line.item) return
    const r = await suggestPriceApi(line.item)
    if (!r) return
    updateLine(idx, {
      unit_price: line.sale_type === 'sale' ? Number(r.unit_price) : 0,
      ...previousPatch(r),
      ...monthQtyPatch(r),
    })
  }

  // 출고일자(order_date) 변경 시: 이미 입력된 품목 라인의 단가를 "새 출고일자" 기준으로
  // 다시 조회한다. 단가 조회는 항상 출고일자 기준이어야 하는데(작업일이 아님), 화면에서
  // 날짜를 뒤늦게 바꾸면 기존 라인 단가가 옛 날짜(대개 오늘=작업일) 기준으로 굳는 버그 수정.
  // DB 단가가 그 날짜에 존재할 때(source==='partner_price')만 덮어써 수동 입력 단가는 보존.
  async function repriceLinesForDate(date: string) {
    if (!edit?.partner || !edit.lines.length) return
    const results = await Promise.all(
      edit.lines.map((l) => (l.item ? suggestPriceApi(l.item, date) : Promise.resolve(null))),
    )
    setEdit((cur) => {
      if (!cur) return cur
      return {
        ...cur,
        lines: cur.lines.map((l, i) => {
          const r = results[i]
          if (!r) return l
          const patch: Partial<LineForm> = { ...previousPatch(r), ...monthQtyPatch(r) }
          if (l.sale_type === 'sale' && r.source === 'partner_price' && r.unit_price != null) {
            patch.unit_price = Number(r.unit_price)
          }
          return { ...l, ...patch }
        }),
      }
    })
  }

  // 모달이 열리면 (edit 이 null → 값으로 전환) 자동으로 출고일자 focus.
  const editOpen = !!edit
  useEffect(() => {
    if (editOpen) focusFormField('order_date')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editOpen])

  // 기존 전표 편집 열림 시: 라인별 직전단가·월누계만 조회 (단가는 유지).
  // 출고는 전표 단위 month-qtys 1번으로 — 라인마다 suggest-price 를 동시에 부르면(40줄 = 40요청)
  // 서버 처리 자리가 차서 저장·목록까지 밀렸음(2026-10-29). month-qtys 가 없는 입고는 기존대로.
  useEffect(() => {
    if (!edit?.id || !edit.partner || !edit.lines.length) return
    let cancelled = false
    ;(async () => {
      const patches = c.monthQtyRef
        ? await api<MonthQtyBatch>(`${c.apiBase}/${edit.id}/month-qtys`).then((d) => edit.lines.map((l) => {
            const m = l.id != null ? d.lines[l.id] : undefined
            if (!m) return null
            return {
              ...previousPatch({
                show_previous: d.show_previous,
                previous_unit_price: m.previous_unit_price ?? undefined,
                previous_order_date: m.previous_order_date ?? undefined,
                previous_unit: m.previous_unit ?? undefined,
              }),
              ...monthQtyPatch({
                curr_month_qty: m.curr_month_qty, prev_month_qty: m.prev_month_qty,
                curr_month_label: d.curr_month_label, prev_month_label: d.prev_month_label,
                last_out_price: m.last_out_price ?? undefined, last_out_date: m.last_out_date ?? undefined,
                last_out_conflict: m.last_out_conflict, last_out_detail: m.last_out_detail,
              }),
            }
          })).catch(() => edit.lines.map(() => null))
        : await Promise.all(edit.lines.map(async (l) => {
          if (!l.item) return null
          const r = await suggestPriceApi(l.item)
          return { ...previousPatch(r), ...monthQtyPatch(r) }
        }))
      if (cancelled) return
      setEdit((cur) => {
        if (!cur) return cur
        return {
          ...cur,
          lines: cur.lines.map((l, i) => patches[i] ? { ...l, ...patches[i]! } : l),
        }
      })
    })()
    return () => { cancelled = true }
    // partner/order_date/id 만 의존 — 라인 편집 중 재실행 방지
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edit?.id])

  // 출고유형 변경: 매출 외 = 0원 고정, 매출로 되돌리면 등록 단가를 다시 불러온다.
  // 새 줄 기본값(전표 기본 유형) — 창마다 따로, 새로 열면 다시 '매출'.
  const editKey = edit?.id ?? (editOpen ? 'new' : null)
  const [saleDefaultOf, setSaleDefaultOf] = useState<{ key: number | string | null; type: SaleType }>({ key: null, type: 'sale' })
  const saleDefault: SaleType = saleDefaultOf.key === editKey ? saleDefaultOf.type : 'sale'
  const salePatch = (t: SaleType): Partial<LineForm> =>
    (t === 'sale' ? { sale_type: t } : { sale_type: t, unit_price: 0, manual_amount: false })
  function changeSaleType(i: number, line: LineForm, t: SaleType) {
    updateLine(i, salePatch(t))
    if (t === 'sale') void fillPrice(i, { ...line, sale_type: t })
  }
  function changeSaleDefault(t: SaleType) {
    setSaleDefaultOf({ key: editKey, type: t })
    setEdit((cur) => cur ? { ...cur, lines: cur.lines.map((l) => ({ ...l, ...salePatch(t) })) } : cur)
    if (t === 'sale') edit?.lines.forEach((l, i) => { void fillPrice(i, { ...l, sale_type: t }) })
  }

  function updateLine(i: number, patch: Partial<LineForm>) {
    // 함수형 업데이트 필수: 품목 선택 직후 setTimeout(fillPrice) 가 다시 updateLine 을
    // 호출하는데, 클로저의 옛 edit(item=null)를 쓰면 방금 선택한 품목이 덮여 사라진다.
    setEdit((cur) => {
      if (!cur) return cur
      const lines = [...cur.lines]; lines[i] = { ...lines[i], ...patch }
      return { ...cur, lines }
    })
  }
  function addLine(focusFieldName: FieldName = 'item-trigger') {
    if (!edit) return
    const newIdx = edit.lines.length
    setEdit((cur) => cur ? { ...cur, lines: [...cur.lines, { ...blankLine(), sale_type: saleDefault }] } : cur)
    focusField(newIdx, focusFieldName)
  }
  // i 번 라인 '바로 아래'에 새 라인 삽입 (중간 삽입).
  function insertLineAfter(i: number) {
    setEdit((cur) => {
      if (!cur) return cur
      const lines = [...cur.lines]
      lines.splice(i + 1, 0, { ...blankLine(), sale_type: saleDefault })
      return { ...cur, lines }
    })
    focusField(i + 1, 'item-trigger')
  }
  function removeLine(i: number) {
    setEdit((cur) => cur ? { ...cur, lines: cur.lines.filter((_, idx) => idx !== i) } : cur)
  }

  // ── 라인 필드 focus 헬퍼 (키보드 워크플로우) ──
  const linesTableRef = useRef<HTMLTableElement>(null)
  const formGridRef = useRef<HTMLDivElement>(null)
  const addLineBtnRef = useRef<HTMLButtonElement>(null)
  const cancelBtnRef = useRef<HTMLButtonElement>(null)
  const saveBtnRef = useRef<HTMLButtonElement>(null)
  function focusAddLineBtn() {
    requestAnimationFrame(() => addLineBtnRef.current?.focus())
  }
  function focusUpFromBottomBtns() {
    // 취소/저장에서 ↑ — 마지막 라인 unit-price 로, 라인 없으면 라인 추가 버튼으로.
    if (edit && edit.lines.length > 0) focusField(edit.lines.length - 1, 'unit-price')
    else addLineBtnRef.current?.focus()
  }
  function focusField(idx: number, field: FieldName) {
    // DOM 반영 후 focus (라인 추가 후 새 행이 렌더되는 시간 확보)
    requestAnimationFrame(() => {
      const cell = linesTableRef.current?.querySelector<HTMLElement>(
        `tr[data-line-idx="${idx}"] [data-field="${field}"]`,
      )
      if (!cell) return
      const target = cell.tagName === 'INPUT' || cell.tagName === 'BUTTON'
        ? cell
        : cell.querySelector<HTMLElement>('input, button')
      if (!target) return
      target.focus()
      if (target instanceof HTMLInputElement) target.select()
    })
  }
  function focusFormField(name: FormField) {
    requestAnimationFrame(() => {
      const cell = formGridRef.current?.querySelector<HTMLElement>(
        `[data-form-field="${name}"]`,
      )
      if (!cell) return
      const target = ['INPUT', 'BUTTON', 'SELECT'].includes(cell.tagName)
        ? cell
        : cell.querySelector<HTMLElement>('input, button, select')
      if (!target) return
      target.focus()
      if (target instanceof HTMLInputElement && target.type !== 'date') target.select()
    })
  }
  function formKeyDown(e: React.KeyboardEvent) {
    const el = e.target instanceof HTMLElement ? e.target : null
    if (!el || !formGridRef.current?.contains(el)) return
    const fieldEl = el.closest<HTMLElement>('[data-form-field]')
    const rawField = fieldEl?.getAttribute('data-form-field')
    if (!rawField || !(FORM_FIELDS as readonly string[]).includes(rawField)) return
    const field = rawField as FormField
    const cur = FORM_FIELDS.indexOf(field)

    // 편의: 텍스트 input(적요) 은 커서 위치 확인, select/date 는 native ↑↓ 유지.
    const isText = el instanceof HTMLInputElement && (el.type === 'text' || el.type === '')
    const atStart = !isText || (el.selectionStart === 0 && el.selectionEnd === 0)
    const atEnd = !isText || (el.selectionEnd === el.value.length)
    const isSelectOrDate = el instanceof HTMLSelectElement
      || (el instanceof HTMLInputElement && el.type === 'date')

    function goLinearPrev() {
      const prev = FORM_FIELDS[cur - 1]
      if (prev) focusFormField(prev)
    }
    function goLinearNext() {
      const next = FORM_FIELDS[cur + 1]
      if (next) focusFormField(next)
      else if (edit && edit.lines.length > 0) focusField(0, 'item-trigger')
    }
    function goVerticalDown() {
      const dest = DOWN_MAP[field]
      if (dest === 'add-line') focusAddLineBtn()
      else if (dest) focusFormField(dest)
    }
    function goVerticalUp() {
      const dest = UP_MAP[field]
      if (dest) focusFormField(dest)
    }

    if (e.key === 'ArrowRight' && atEnd) { e.preventDefault(); goLinearNext() }
    else if (e.key === 'ArrowLeft' && atStart) { e.preventDefault(); goLinearPrev() }
    else if (e.key === 'ArrowDown' && !isSelectOrDate) { e.preventDefault(); goVerticalDown() }
    else if (e.key === 'ArrowUp' && !isSelectOrDate) { e.preventDefault(); goVerticalUp() }
  }

  // 라인 필드 focus 대상(FocusTarget) 실행 — 결정 로직은 @/lib/lineNav.decideRowNav.
  function execFocusTarget(t: import('@/lib/lineNav').FocusTarget) {
    switch (t.kind) {
      case 'field': focusField(t.index, t.field); break
      case 'addLine': addLine(t.focus); break
      case 'addLineBtn': focusAddLineBtn(); break
      case 'saveBtn': saveBtnRef.current?.focus(); break
    }
  }

  // 라인 내 필드 순서 — ← → 이동, ↑ ↓ 행 이동에 사용.
  function rowKeyDown(e: React.KeyboardEvent, i: number) {
    if (!edit) return
    const el = e.target instanceof HTMLElement ? e.target : null
    if (!el) return
    // 포탈로 뜬 Modal(ItemPicker 검색창 등) 이벤트는 무시 — 실제 DOM 상 라인 테이블 밖.
    if (!linesTableRef.current?.contains(el)) return
    const fieldEl = el.closest<HTMLElement>('[data-field]')
    const rawField = fieldEl?.getAttribute('data-field')
    if (!rawField || !(FIELDS as readonly string[]).includes(rawField)) return
    const field = rawField as FieldName

    // 텍스트 입력창(spec/unit) 은 커서가 끝(← 는 시작)에 있을 때만 필드 이동.
    const isText = el instanceof HTMLInputElement && (el.type === 'text' || el.type === '')
    const atStart = !isText || (el.selectionStart === 0 && el.selectionEnd === 0)
    const atEnd = !isText || (el.selectionEnd === el.value.length)
    const isTextWithContent = isText && el instanceof HTMLInputElement && el.value.length > 0

    const action = decideRowNav({
      key: e.key, ctrlKey: e.ctrlKey, metaKey: e.metaKey, shiftKey: e.shiftKey,
      field, index: i, linesLength: edit.lines.length,
      atStart, atEnd, isTextWithContent,
    })
    if (action.type === 'none') return
    e.preventDefault()
    if (action.type === 'deleteLine') {
      removeLine(action.index)
      // 삭제로 행이 사라진 뒤 DOM 재렌더를 한 프레임 기다렸다 focus.
      requestAnimationFrame(() => execFocusTarget(action.then))
      return
    }
    execFocusTarget(action.target)
  }

  function trySave() {
    if (!edit || savingRef.current || save.isPending) return
    if (!edit.order_date) { push(`${c.entityDate}를 선택하세요`, 'warn'); return }
    if (!edit.partner) { push(`${c.partnerLabel}를 선택하세요`, 'warn'); return }
    if (!edit.lines.length || edit.lines.some((l) => !l.item)) {
      push('품목 라인을 모두 채우세요', 'warn'); return
    }
    savingRef.current = true
    save.mutate(edit)
  }
  function lineSupply(l: LineForm) {
    return l.manual_amount ? Number(l.supply_amount || 0) : (Number(l.qty) || 0) * Number(l.unit_price || 0)
  }
  function lineVat(l: LineForm) {
    if (l.manual_amount) return Number(l.vat_amount || 0)
    return edit?.vat_type === 'vat' ? lineSupply(l) * 0.1 : 0
  }
  // 선택 라인 날짜분리: Delivery 전용 + 기존 전표 수정일 때만.
  const showDateSplit = !!c.lineDateSplit && !!edit?.id
  const editSubtotal = edit?.lines.reduce((s, l) => s + lineSupply(l), 0) ?? 0
  const editVat = edit?.lines.reduce((s, l) => s + lineVat(l), 0) ?? 0
  const editTotal = editSubtotal + editVat

  const listColCount = c.invoicePrint ? 11 : 10

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-4 flex items-center gap-2 justify-end">
        {c.invoicePrint && (
          <button onClick={() => openInvoicePrint()} disabled={!anySelected}
                  title={anySelected ? '' : '전표를 선택하세요'}
                  className="bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed border border-slate-300 text-slate-700 text-sm font-semibold rounded-lg px-3 py-1.5 inline-flex items-center gap-1.5">
            <FileText className="w-4 h-4"/>
            거래명세서 인쇄 {anySelected ? `(${selected.size}건)` : ''}
          </button>
        )}
        {c.invoicePrint && anySelected && (
          <>
            <button onClick={() => { setMoveDate(''); setMoveOpen(true) }}
                    className="bg-white hover:bg-amber-50 border border-amber-300 text-amber-700 text-sm font-semibold rounded-lg px-3 py-1.5 inline-flex items-center gap-1.5">
              <CalendarClock className="w-4 h-4"/>
              날짜 이동 ({selected.size}건)
            </button>
            <button onClick={() => { setCopyDate(''); setCopyOpen(true) }}
                    title="선택 전표를 다른 날짜로 복제(원본 유지)"
                    className="bg-white hover:bg-sky-50 border border-sky-300 text-sky-700 text-sm font-semibold rounded-lg px-3 py-1.5 inline-flex items-center gap-1.5">
              <Copy className="w-4 h-4"/>
              날짜 복사 ({selected.size}건)
            </button>
            <button onClick={doBulkDelete} disabled={bulkDelete.isPending}
                    className="bg-white hover:bg-rose-50 disabled:opacity-50 border border-rose-300 text-rose-600 text-sm font-semibold rounded-lg px-3 py-1.5 inline-flex items-center gap-1.5">
              {bulkDelete.isPending ? <Loader2 className="w-4 h-4 animate-spin"/> : <Trash2 className="w-4 h-4"/>}
              선택 삭제 ({selected.size}건)
            </button>
          </>
        )}
        <button onClick={downloadExcel} disabled={downloading || !orders.length}
                className="bg-white hover:bg-slate-50 disabled:opacity-50 border border-slate-300 text-slate-700 text-sm font-semibold rounded-lg px-3 py-1.5 inline-flex items-center gap-1.5">
          {downloading ? <Loader2 className="w-4 h-4 animate-spin"/> : <Download className="w-4 h-4"/>}
          엑셀 다운로드
        </button>
        {c.excelImport && (
          <>
            <input ref={excelInputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={onExcelPicked} />
            <button onClick={() => excelInputRef.current?.click()} disabled={importExcel.isPending}
                    title="더존 엑셀 양식(그룹번호로 상단·하단 조인) 업로드 → 전표 일괄 등록. 한 파일에 여러 날짜 가능"
                    className="bg-white hover:bg-slate-50 disabled:opacity-50 border border-slate-300 text-slate-700 text-sm font-semibold rounded-lg px-3 py-1.5 inline-flex items-center gap-1.5">
              {importExcel.isPending ? <Loader2 className="w-4 h-4 animate-spin"/> : <Upload className="w-4 h-4"/>}
              엑셀로 추가
            </button>
          </>
        )}
        <button onClick={() => setEdit(blankOrder())}
                data-role="add-btn"
                title="Alt+N"
                className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg px-3 py-1.5 inline-flex items-center gap-1.5">
          <Plus className="w-4 h-4" /> {c.entity} 추가
        </button>
      </div>

      {/* 선택 전표 날짜 이동 모달 */}
      {moveOpen && (
        <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4"
             onClick={() => setMoveOpen(false)}>
          <div className="bg-white rounded-xl p-5 w-80 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-semibold text-slate-800 mb-1 flex items-center gap-1.5">
              <CalendarClock className="w-4 h-4 text-amber-600"/> 선택 {selected.size}건 날짜 이동
            </h3>
            <p className="text-xs text-slate-500 mb-3">
              선택한 {c.entity}의 {c.entityDate}를 아래 날짜로 옮깁니다. {c.entityNo}도 새 날짜로 다시 부여됩니다.
            </p>
            <input type="date" value={moveDate} autoFocus
                   onChange={(e) => setMoveDate(e.target.value)}
                   onKeyDown={(e) => { if (e.key === 'Enter') doBulkMove() }}
                   className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mb-4 focus:border-amber-500 focus:ring-2 focus:ring-amber-100 outline-none"/>
            <div className="flex justify-end gap-2">
              <button onClick={() => setMoveOpen(false)}
                      className="text-sm text-slate-600 hover:text-slate-800 px-3 py-1.5">취소</button>
              <button onClick={doBulkMove} disabled={!moveDate || bulkMove.isPending}
                      className="bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white text-sm font-semibold rounded-lg px-4 py-1.5 inline-flex items-center gap-1.5">
                {bulkMove.isPending ? <Loader2 className="w-4 h-4 animate-spin"/> : <CalendarClock className="w-4 h-4"/>}
                이동
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 선택 전표 날짜 복사 모달 (다른 날짜로만) */}
      {copyOpen && (
        <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4"
             onClick={() => setCopyOpen(false)}>
          <div className="bg-white rounded-xl p-5 w-80 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-semibold text-slate-800 mb-1 flex items-center gap-1.5">
              <Copy className="w-4 h-4 text-sky-600"/> 선택 {selected.size}건 날짜 복사
            </h3>
            <p className="text-xs text-slate-500 mb-3">
              선택한 {c.entity}를 아래 날짜로 <b>복제</b>합니다(원본 유지, 품목·수량·단가·업장 동일).
              원본과 <b>다른 날짜만</b> 가능합니다.
            </p>
            <input type="date" value={copyDate} autoFocus
                   onChange={(e) => setCopyDate(e.target.value)}
                   onKeyDown={(e) => { if (e.key === 'Enter') doBulkCopy() }}
                   className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mb-4 focus:border-sky-500 focus:ring-2 focus:ring-sky-100 outline-none"/>
            <div className="flex justify-end gap-2">
              <button onClick={() => setCopyOpen(false)}
                      className="text-sm text-slate-600 hover:text-slate-800 px-3 py-1.5">취소</button>
              <button onClick={doBulkCopy} disabled={!copyDate || bulkCopy.isPending}
                      className="bg-sky-600 hover:bg-sky-700 disabled:opacity-50 text-white text-sm font-semibold rounded-lg px-4 py-1.5 inline-flex items-center gap-1.5">
                {bulkCopy.isPending ? <Loader2 className="w-4 h-4 animate-spin"/> : <Copy className="w-4 h-4"/>}
                복사
              </button>
            </div>
          </div>
        </div>
      )}

      <DateRangeBar from={dateFrom} to={dateTo} onFrom={setDateFrom} onTo={setDateTo}>
        <RefreshButton busy={isFetching} onClick={() => { void refetch() }} />
        <SearchBox value={search} onChange={setSearch}
                   placeholder={c.searchPlaceholder} className="flex-1 min-w-[200px]"/>
        <span className="text-xs text-slate-400 tabular-nums">{orders.length}건</span>
      </DateRangeBar>

      {/* 목록에 자체 스크롤 영역을 줘서 헤더 행을 컨테이너 상단에 고정(sticky). 페이지
          스크롤/필터바 높이에 의존하지 않아 안정적. */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-auto max-h-[calc(100vh-230px)]">
        <table className="w-full text-sm">
          <thead ref={listHeadRef} className="text-xs uppercase text-slate-500 tracking-wide sticky top-0 z-10">
            <tr className="bg-slate-50 [&>th]:bg-slate-50 border-b border-slate-200 shadow-[0_1px_0_rgba(0,0,0,0.06)]">
              {c.invoicePrint && (
                <th className="px-3 py-2.5 w-8 text-center">
                  <input type="checkbox" checked={allSelected} onChange={toggleAll}
                         disabled={!orders.length} className="accent-emerald-600 cursor-pointer"
                         title="전체 선택 / 해제 (행은 Shift+클릭으로 범위 선택)"/>
                </th>
              )}
              <th className="px-3 py-2.5 w-8"></th>
              <th className="px-3 py-2.5 text-center font-medium w-12 text-slate-400 normal-case">순번</th>
              {listSortTh('order_date', c.entityDate, 'px-3 py-2.5 text-left font-medium w-28')}
              {listSortTh('order_no', c.entityNo, 'px-3 py-2.5 text-left font-medium w-44')}
              {listSortTh('partner', c.partnerLabel, 'px-3 py-2.5 text-left font-medium')}
              {listSortTh('count', '품목수', 'px-3 py-2.5 text-center font-medium w-16')}
              {listSortTh('subtotal', '공급가액', 'px-3 py-2.5 text-right font-medium w-28')}
              {listSortTh('vat', '부가세', 'px-3 py-2.5 text-right font-medium w-24')}
              {listSortTh('total', '합계', 'px-3 py-2.5 text-right font-medium w-28')}
              <th className="px-3 py-2.5 text-right font-medium w-20"></th>
            </tr>
          </thead>
          <tbody>
            {sortedOrders.map((o, idx) => (
              <Fragment key={o.id}>
                <tr ref={(el) => { rowRefs.current[o.id] = el }}
                    className={cn('border-t border-slate-100 hover:bg-slate-50 transition-colors',
                      highlight === o.id && 'bg-yellow-100',
                      movedIds.has(o.id) && 'animate-move-flash',
                      c.invoicePrint && selected.has(o.id) && 'bg-emerald-50/60')}>
                  {c.invoicePrint && (
                    <td className="px-3 py-2 text-center">
                      <input type="checkbox" checked={selected.has(o.id)} readOnly
                             onClick={(e) => toggleOne(o.id, idx, e.shiftKey)}
                             title="Shift+클릭: 이전 선택부터 여기까지 한 번에"
                             className="accent-emerald-600 cursor-pointer"/>
                    </td>
                  )}
                  <td className="px-3 py-2">
                    <button onClick={() => {
                              const next = expanded === o.id ? null : o.id
                              setExpanded(next)
                              if (next != null) void ensureLines(o)   // 펼칠 때 라인 확보
                            }}
                            className="text-slate-400 hover:text-slate-700">
                      <ChevronRight className={cn('w-4 h-4 transition-transform', expanded === o.id && 'rotate-90')} />
                    </button>
                  </td>
                  <td className="px-3 py-2 text-center text-xs tabular-nums text-slate-400 select-none">{idx + 1}</td>
                  <td className="px-3 py-2 tabular-nums text-slate-600">{o.order_date}</td>
                  <td className="px-3 py-2 font-mono text-xs tabular-nums text-slate-700">{o.order_no}</td>
                  <td className="px-3 py-2 text-slate-900 font-medium">
                    {o.partner_name}
                    {o.is_carryover && (
                      <span className="ml-1.5 align-middle text-[10px] font-semibold text-sky-700 bg-sky-100 border border-sky-200 rounded px-1 py-0.5">이월</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center text-xs tabular-nums text-slate-600">
                    {o.lines_count ?? o.lines?.length ?? linesById[o.id]?.length ?? 0}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatNum(o.subtotal)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-500">{formatNum(o.vat_total)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold">{formatNum(o.total)}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => { void openFresh(o).then((f) => f && setEdit({
                      id: f.id, order_date: f.order_date, partner: f.partner,
                      vat_type: f.vat_type, tax_type: f.tax_type, note: f.note,
                      is_carryover: f.is_carryover ?? false,
                      lines: (f.lines ?? []).map((l) => ({
                        id: l.id,
                        item: l.item, item_code: l.item_code, item_name: l.item_name,
                        spec: l.spec, unit: l.unit,
                        qty: Number(l.qty), unit_price: Number(l.unit_price), note: l.note, note2: l.note2 ?? '',
                        manual_amount: l.manual_amount ?? false,
                        supply_amount: Number(l.supply_amount), vat_amount: Number(l.vat_amount),
                        is_prepaid: l.is_prepaid ?? false, subdivide: l.subdivide ?? 0,
                        sale_type: l.sale_type ?? 'sale',
                      })),
                    })) }} className="text-blue-600 hover:text-blue-800 text-xs mr-2">수정</button>
                    <button onClick={() => { void confirm(`${o.order_no} 전표를 삭제할까요?`, { danger: true }).then((ok) => ok && remove.mutate(o.id)) }}
                            className="text-rose-500 hover:text-rose-700 text-xs">삭제</button>
                  </td>
                </tr>
                {expanded === o.id && (
                  <tr className="bg-slate-50/70">
                    <td colSpan={listColCount} className="px-12 py-3">
                      <div className="text-xs text-slate-500 mb-1.5">
                        {c.linesTitle} ({linesOf(o)?.length ?? o.lines_count ?? 0}건)
                        {!linesOf(o) && <span className="ml-2 text-slate-400">불러오는 중…</span>}
                      </div>
                      <table className="w-full text-xs">
                        {/* 상세 라인 헤더(연두색)도 sticky — 상단 노란색 목록 헤더 바로
                            아래(측정한 실제 높이만큼 오프셋, 1px 겹쳐 틈 방지)에 고정해,
                            62건 등 긴 라인을 스크롤해도 컬럼명이 계속 보이게 한다. th마다
                            배경을 줘야 sticky 시 아래 행이 비쳐 보이지 않는다. */}
                        <thead className="sticky z-[5]" style={{ top: Math.max(0, listHeadH - 1) }}>
                          <tr className="text-slate-500 bg-emerald-50 [&>th]:bg-emerald-50 [&>th]:pt-1.5 border-b border-emerald-200 shadow-[0_1px_0_rgba(0,0,0,0.06)]">
                            <th className="text-left font-medium pb-1 w-28">품목코드</th>
                            <th className="text-left font-medium pb-1">품명</th>
                            <th className="text-left font-medium pb-1 w-24">{c.specColLabel}</th>
                            <th className="text-left font-medium pb-1 w-12">단위</th>
                            <th className="text-right font-medium pb-1 w-20">수량</th>
                            <th className="text-right font-medium pb-1 w-24">단가</th>
                            <th className="text-right font-medium pb-1 w-28">공급가액</th>
                            {c.monthQtyRef && (<>
                              <th className="text-right font-medium pb-1 w-32 pl-4 border-l border-slate-200 text-emerald-600 whitespace-nowrap">
                                직전출고단가
                              </th>
                              <th className="text-right font-medium pb-1 w-24 pl-4 text-slate-400 whitespace-nowrap">
                                저번달누계
                              </th>
                              <th className="text-right font-medium pb-1 w-24 pl-4 text-slate-400 whitespace-nowrap">
                                이번달누계
                              </th>
                            </>)}
                          </tr>
                        </thead>
                        <tbody>
                          {(linesOf(o) ?? []).map((l) => {
                            const mq = monthQtys[o.id]?.lines[l.id]
                            // 직전출고단가 대비 현재 단가가 ±50% 이상 변동이면 노란 음영
                            // (단가 오입력 신호). 둘 다 0 초과일 때만 판정.
                            const prevPrice = mq?.last_out_price
                            const bigPriceChange = prevPrice != null && Number(prevPrice) > 0
                              && Number(l.unit_price) > 0
                              && Math.abs(Number(l.unit_price) - Number(prevPrice)) / Number(prevPrice) >= 0.5
                            return (
                            <tr key={l.id} className="text-slate-700">
                              <td className="py-1 font-mono tabular-nums text-slate-500">{l.item_code}</td>
                              <td className="py-1">
                                {l.item_name}
                                {l.sale_type && l.sale_type !== 'sale' && (
                                  <span className="ml-1.5 text-[10px] px-1 py-0.5 rounded bg-violet-100 text-violet-700 font-semibold">
                                    {SALE_TYPES.find(([v]) => v === l.sale_type)?.[1]}
                                  </span>
                                )}
                              </td>
                              <td className="py-1 text-slate-500">{l.spec}</td>
                              <td className="py-1 text-slate-500">{l.unit}</td>
                              <td className="py-1 text-right tabular-nums">
                                <span className={cn(isLargeQty(l.qty) ? 'bg-red-200 text-red-700 rounded px-1'
                                  : isFractionalCountUnit(l.unit, l.qty) && 'bg-yellow-200 rounded px-1')}>{formatNum(l.qty)}</span>
                              </td>
                              <td className="py-1 text-right tabular-nums">{formatNum(l.unit_price)}</td>
                              <td className="py-1 text-right tabular-nums font-semibold">{formatNum(l.supply_amount)}</td>
                              {c.monthQtyRef && (<>
                                <td className={cn('py-1 text-right tabular-nums pl-4 border-l border-slate-200 whitespace-nowrap',
                                                  mq?.last_out_conflict ? 'text-rose-600 font-semibold' : 'text-emerald-700')}>
                                  {mq?.last_out_price != null
                                    ? <span
                                        title={mq.last_out_conflict
                                          ? `업장별 단가 상이:\n${mq.last_out_detail}`
                                          : bigPriceChange
                                            ? `직전출고단가 ${formatNum(prevPrice)} 대비 ${Math.round((Number(l.unit_price) - Number(prevPrice)) / Number(prevPrice) * 100)}% 변동`
                                            : undefined}
                                        className={cn(bigPriceChange && 'bg-yellow-200 rounded px-1')}>
                                        {mq.last_out_conflict && '⚠ '}{formatNum(mq.last_out_price)}
                                        {mq.last_out_date && <span className="text-slate-400 ml-1">({mq.last_out_date})</span>}
                                      </span>
                                    : ''}
                                </td>
                                <td className="py-1 text-right tabular-nums text-slate-500 pl-4">
                                  {mq ? formatNum(mq.prev_month_qty) : (monthQtys[o.id] ? '0' : '')}
                                </td>
                                <td className="py-1 text-right tabular-nums text-slate-500 pl-4">
                                  {mq ? formatNum(mq.curr_month_qty) : (monthQtys[o.id] ? '0' : '')}
                                </td>
                              </>)}
                            </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
            {!orders.length && (
              <tr><td colSpan={listColCount} className="text-center text-slate-400 py-10 text-sm">{c.emptyText}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal open={!!edit} onClose={() => setEdit(null)}
             title={edit?.id ? `${c.entity} 수정` : `${c.entity} 추가`}
             size={c.purchaseFields ? 'wide' : '7xl'}>
        {edit && (
          <div onKeyDown={(e) => {
            // Ctrl/Cmd + Enter → 저장
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
              e.preventDefault(); trySave(); return
            }
            // 상단 폼 방향키 이동
            formKeyDown(e)
          }}>
            <div ref={formGridRef} className="grid grid-cols-4 gap-3 mb-5">
              <div data-form-field="order_date">
                <label className="text-xs font-semibold text-slate-600 mb-1 block">{c.entityDate} *</label>
                <input type="date" value={edit.order_date}
                       onChange={(e) => {
                         const nd = e.target.value
                         setEdit((cur) => (cur ? { ...cur, order_date: nd } : cur))
                         void repriceLinesForDate(nd)   // 새 출고일자 기준으로 라인 단가 재조회
                       }}
                       className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm tabular-nums"/>
              </div>
              <div className="col-span-2" data-form-field="partner">
                <label className="text-xs font-semibold text-slate-600 mb-1 block">{c.partnerLabel} *</label>
                <PartnerPicker value={edit.partner} onChange={(p) => setEdit({ ...edit, partner: p.id })}
                               bizClass={c.partnerBizClass ?? ''} />
              </div>
              <div data-form-field="vat_type">
                <label className="text-xs font-semibold text-slate-600 mb-1 block">VAT 여부</label>
                <select value={edit.vat_type}
                        onChange={(e) => setEdit({ ...edit, vat_type: e.target.value as 'vat' | 'none' })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white">
                  <option value="none">VAT 없음</option>
                  <option value="vat">VAT 포함</option>
                </select>
              </div>
              <div data-form-field="tax_type">
                <label className="text-xs font-semibold text-slate-600 mb-1 block">과세구분</label>
                <select value={edit.tax_type}
                        onChange={(e) => setEdit({ ...edit, tax_type: e.target.value as 'taxable' | 'exempt' | 'zero' })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white">
                  <option value="exempt">면세</option>
                  <option value="taxable">과세</option>
                  <option value="zero">영세</option>
                </select>
              </div>
              <div className={cn(c.carryoverFlag ? 'col-span-2' : 'col-span-3')} data-form-field="note">
                <label className="text-xs font-semibold text-slate-600 mb-1 block">적요</label>
                <input value={edit.note} onChange={(e) => setEdit({ ...edit, note: e.target.value })}
                       className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"/>
              </div>
              {c.carryoverFlag && (
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1 block">이월</label>
                  <label className="flex items-center gap-2 px-3 py-2 border border-slate-300 rounded-lg text-sm cursor-pointer select-none"
                         title="체크 시 이 전표 금액이 매출관리 일일매출에서 다음달 이월(전일)로 반영됩니다. 실제 출고·판매월보엔 영향 없음.">
                    <input type="checkbox" checked={!!edit.is_carryover}
                           onChange={(e) => setEdit({ ...edit, is_carryover: e.target.checked })}/>
                    <span className={cn(edit.is_carryover ? 'text-sky-700 font-semibold' : 'text-slate-500')}>다음달 이월</span>
                  </label>
                </div>
              )}
            </div>

            <div className="border border-slate-200 rounded-xl overflow-x-auto">
              <div className="bg-slate-50 px-3 py-2 flex items-center gap-2 border-b border-slate-200">
                <span className="text-xs font-bold text-slate-600 uppercase tracking-wide">품목 라인</span>
                {c.purchaseFields && (
                  <label className="ml-3 text-xs text-slate-500 flex items-center gap-1"
                         title="고르면 지금 있는 줄과 새 줄 모두 이 유형으로 바뀝니다. 줄마다 따로 바꿀 수도 있습니다.">
                    전표 기본 유형
                    <select value={saleDefault} onChange={(e) => changeSaleDefault(e.target.value as SaleType)}
                            className="px-1.5 py-0.5 border border-slate-300 rounded text-xs bg-white">
                      {SALE_TYPES.map(([v, lbl]) => <option key={v} value={v}>{lbl}</option>)}
                    </select>
                  </label>
                )}
                <button ref={addLineBtnRef}
                        onClick={() => addLine()}
                        onKeyDown={(e) => {
                          if (e.key === 'ArrowDown') {
                            e.preventDefault()
                            if (edit && edit.lines.length > 0) focusField(0, 'item-trigger')
                            else cancelBtnRef.current?.focus()  // 라인 없으면 하단 취소로
                          } else if (e.key === 'ArrowUp') {
                            e.preventDefault(); focusFormField('note')
                          } else if (e.key === 'ArrowRight') {
                            e.preventDefault(); cancelBtnRef.current?.focus()  // 오른쪽 → 취소
                          }
                          // Enter/Space 는 브라우저 native 로 클릭 발동 → addLine 실행
                        }}
                        className="ml-auto text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded px-2.5 py-1 focus:ring-2 focus:ring-emerald-300 focus:outline-none">
                  + 라인 추가
                </button>
              </div>
              {showDateSplit && (
                <div className="bg-amber-50 border-b border-amber-200 px-3 py-2 flex items-center gap-2 flex-wrap text-xs">
                  <span className="font-semibold text-amber-800">선택 품목만 날짜 이동</span>
                  <span className="text-slate-500 tabular-nums">선택 {splitSel.size}개</span>
                  <span className="text-slate-400">→</span>
                  <input type="date" value={splitDate}
                         onChange={(e) => setSplitDate(e.target.value)}
                         title={`옮길 ${c.entityDate}`}
                         className="px-2 py-1 border border-slate-300 rounded text-xs tabular-nums bg-white"/>
                  <button type="button" onClick={applyDateSplit}
                          disabled={splitSel.size === 0 || !splitDate || splitLines.isPending}
                          className="text-xs font-semibold rounded px-2.5 py-1 bg-amber-600 hover:bg-amber-700 disabled:opacity-40 text-white">
                    {splitLines.isPending ? '이동 중...' : '적용'}
                  </button>
                  <span className="text-[11px] text-slate-400">
                    체크한 품목만 그 날짜 전표로 이동(그 날짜에 같은 거래처 전표 있으면 병합).
                  </span>
                </div>
              )}
              <table ref={linesTableRef} className="w-full text-sm">
                <thead className="text-xs text-slate-500 bg-slate-50 border-b">
                  <tr>
                    {showDateSplit && <th className="px-2 py-1.5 w-8 text-center"></th>}
                    <th className="px-2 py-1.5 text-left font-medium">품목</th>
                    <th className="px-2 py-1.5 text-left font-medium w-24">{c.specColLabel}</th>
                    <th className="px-2 py-1.5 text-left font-medium w-12">단위</th>
                    <th className="px-2 py-1.5 text-right font-medium w-20">수량</th>
                    <th className="px-2 py-1.5 text-right font-medium w-24">단가</th>
                    <th className="px-2 py-1.5 text-right font-medium w-28">공급가액</th>
                    <th className="px-2 py-1.5 text-right font-medium w-24">부가세</th>
                    <th className="px-2 py-1.5 text-right font-medium w-28">합계</th>
                    {c.purchaseFields && (
                      <>
                        <th className="px-2 py-1.5 text-left font-medium w-20"
                            title="매출 외 유형은 금액 0원, 판매량·판매월보·거래명세서에서 제외, 구매장·라벨·협력업체 발주서에는 포함">유형</th>
                        <th className="px-2 py-1.5 text-center font-medium w-12">
                          <label className="flex flex-col items-center gap-0.5 cursor-pointer" title="전체 선납 토글">
                            <span>선납</span>
                            <input
                              type="checkbox"
                              checked={edit.lines.length > 0 && edit.lines.every((l) => l.is_prepaid)}
                              onChange={(e) => setEdit({
                                ...edit,
                                lines: edit.lines.map((l) => ({ ...l, is_prepaid: e.target.checked })),
                              })}
                            />
                          </label>
                        </th>
                        <th className="px-2 py-1.5 text-center font-medium w-16">소분</th>
                        <th className="px-2 py-1.5 text-left font-medium w-40">비고1<span className="text-slate-400 font-normal">(업장)</span></th>
                        <th className="px-2 py-1.5 text-left font-medium w-40">비고2<span className="text-slate-400 font-normal">(품목)</span></th>
                      </>
                    )}
                    <th className="px-2 py-1.5 w-16"></th>
                  </tr>
                </thead>
                <tbody>
                  {edit.lines.map((line, i) => (
                    <tr key={i} data-line-idx={i} onKeyDown={(e) => rowKeyDown(e, i)}
                        className={cn('border-b border-slate-100',
                          showDateSplit && line.id != null && splitSel.has(line.id) && 'bg-amber-50')}>
                      {showDateSplit && (
                        <td className="px-2 py-1 text-center">
                          {line.id != null && (
                            <input type="checkbox" checked={splitSel.has(line.id)}
                                   title="이 품목을 다른 날짜로 이동 대상에 포함"
                                   onChange={(e) => setSplitSel((cur) => {
                                     const next = new Set(cur)
                                     if (e.target.checked) next.add(line.id!)
                                     else next.delete(line.id!)
                                     return next
                                   })}/>
                          )}
                        </td>
                      )}
                      <td className="px-2 py-1 min-w-[240px]" data-field="item-trigger">
                        <ItemCombo
                          label={line.item ? `${line.item_code} ${line.item_name}` : ''}
                          onChange={(it: Item) => {
                            updateLine(i, {
                              item: it.id, item_code: it.code, item_name: it.name,
                              spec: it.spec, unit: it[c.unitField],
                            })
                            setTimeout(() => {
                              fillPrice(i, { ...line, item: it.id })
                              focusField(i, 'qty')
                            }, 0)
                          }}
                        />
                      </td>
                      <td className="px-2 py-1">
                        <input data-field="spec" value={line.spec} onChange={(e) => updateLine(i, { spec: e.target.value })}
                               className="w-full px-2 py-1 text-xs border border-slate-300 rounded"/>
                      </td>
                      <td className="px-2 py-1">
                        <input data-field="unit" value={line.unit} onChange={(e) => updateLine(i, { unit: e.target.value })}
                               className="w-full px-2 py-1 text-xs border border-slate-300 rounded"/>
                      </td>
                      <td className="px-2 py-1">
                        <input type="text" inputMode="decimal" data-field="qty"
                               value={line.qty === 0 ? '' : line.qty}
                               onFocus={(e) => e.target.select()}
                               onChange={(e) => {
                                 const v = e.target.value
                                 if (v === '') { updateLine(i, { qty: 0 }); return }
                                 // 숫자 + 소수점 3자리까지, 선행 마이너스 허용(반품 출고 음수 수량).
                                 if (!/^-?\d*\.?\d{0,3}$/.test(v)) return
                                 updateLine(i, { qty: v })                // 문자열로 보존(0., -0.02)
                               }}
                               className={cn('no-spinner w-full px-2 py-1 text-sm border rounded text-right tabular-nums',
                                 isLargeQty(line.qty) ? 'bg-red-100 text-red-700 border-red-400 font-semibold' : 'border-slate-300')}/>
                      </td>
                      <td className="px-2 py-1">
                        <input type="number" step="1" data-field="unit-price" value={line.unit_price || ''}
                               disabled={line.sale_type !== 'sale'}
                               onFocus={(e) => e.target.select()}
                               onChange={(e) => updateLine(i, { unit_price: Number(e.target.value) })}
                               className="no-spinner w-full px-2 py-1 text-sm border border-slate-300 rounded text-right tabular-nums disabled:bg-slate-100"/>
                        {line.previous_unit_price != null && (
                          <div className={cn(
                            'text-[10px] mt-0.5 text-right tabular-nums',
                            // 20% 이상 벌어지면 붉게 강조 (오타 신호)
                            Math.abs(line.unit_price - line.previous_unit_price) / line.previous_unit_price > 0.2
                              ? 'text-rose-600 font-semibold'
                              : 'text-amber-700',
                          )}>
                            직전 {formatNum(line.previous_unit_price)}
                            {line.previous_unit && line.previous_unit !== line.unit && (
                              <span className="text-slate-500"> /{line.previous_unit}</span>
                            )}
                            {line.previous_order_date && (
                              <span className="text-slate-400 ml-1">({line.previous_order_date})</span>
                            )}
                          </div>
                        )}
                        {line.last_out_price != null && line.previous_unit_price == null && (
                          <div className={cn('text-[10px] mt-0.5 text-right tabular-nums',
                                             line.last_out_conflict ? 'text-rose-600 font-semibold' : 'text-emerald-700')}
                               title={line.last_out_conflict
                                 ? `상위거래처 업장별 직전단가 상이:\n${line.last_out_detail}`
                                 : '상위거래처 직전 출고(판매) 단가'}>
                            {line.last_out_conflict ? '⚠ ' : '직전출고 '}{formatNum(line.last_out_price)}
                            {line.last_out_date && (
                              <span className="text-slate-400 ml-1">({line.last_out_date})</span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-1">
                        <input type="number" step="1" value={lineSupply(line) || ''}
                               disabled={line.sale_type !== 'sale'}
                               title="직접 입력하면 이 라인은 수기지정(자동계산 안 함)"
                               onFocus={(e) => e.target.select()}
                               onChange={(e) => updateLine(i, line.manual_amount
                                 ? { supply_amount: Number(e.target.value) }
                                 : { manual_amount: true, supply_amount: Number(e.target.value), vat_amount: lineVat(line) })}
                               className={cn('no-spinner w-full px-2 py-1 text-sm border rounded text-right tabular-nums',
                                 line.manual_amount ? 'border-amber-400 bg-amber-50' : 'border-slate-300')}/>
                      </td>
                      <td className="px-2 py-1">
                        <input type="number" step="1" value={lineVat(line) || ''}
                               disabled={line.sale_type !== 'sale'}
                               title="직접 입력하면 이 라인은 수기지정(자동계산 안 함)"
                               onFocus={(e) => e.target.select()}
                               onChange={(e) => updateLine(i, line.manual_amount
                                 ? { vat_amount: Number(e.target.value) }
                                 : { manual_amount: true, vat_amount: Number(e.target.value), supply_amount: lineSupply(line) })}
                               className={cn('no-spinner w-full px-2 py-1 text-sm border rounded text-right tabular-nums',
                                 line.manual_amount ? 'border-amber-400 bg-amber-50' : 'border-slate-300')}/>
                      </td>
                      <td className="px-2 py-1 text-right tabular-nums font-semibold">
                        {formatNum(lineSupply(line) + lineVat(line))}
                        {line.manual_amount && (
                          <button type="button" title="자동계산으로 되돌리기"
                                  onClick={() => updateLine(i, { manual_amount: false })}
                                  className="ml-1 text-[10px] text-amber-600 hover:text-amber-800 align-middle">↺</button>
                        )}
                      </td>
                      {c.purchaseFields && (
                        <>
                          <td className="px-2 py-1">
                            <select value={line.sale_type} data-field="sale-type"
                                    onChange={(e) => changeSaleType(i, line, e.target.value as SaleType)}
                                    className={cn('w-full px-1 py-1 text-xs border rounded bg-white',
                                      line.sale_type === 'sale' ? 'border-slate-300' : 'border-violet-400 text-violet-700 font-semibold')}>
                              {SALE_TYPES.map(([v, lbl]) => <option key={v} value={v}>{lbl}</option>)}
                            </select>
                          </td>
                          <td className="px-2 py-1 text-center">
                            <input type="checkbox" checked={line.is_prepaid}
                                   onChange={(e) => updateLine(i, { is_prepaid: e.target.checked })}/>
                          </td>
                          <td className="px-2 py-1">
                            <input type="number" min="0" step="1" data-field="subdivide"
                                   value={line.subdivide || ''}
                                   onFocus={(e) => e.target.select()}
                                   onChange={(e) => updateLine(i, { subdivide: Math.max(0, Math.floor(Number(e.target.value))) })}
                                   className="no-spinner w-full px-2 py-1 text-sm border border-slate-300 rounded text-right tabular-nums"/>
                          </td>
                          <td className="px-2 py-1">
                            <input data-field="note" value={line.note}
                                   onChange={(e) => updateLine(i, { note: e.target.value })}
                                   placeholder="업장 수정 등"
                                   className="w-full px-2 py-1 text-xs border border-slate-300 rounded"/>
                          </td>
                          <td className="px-2 py-1">
                            <input data-field="note2" value={line.note2}
                                   onChange={(e) => updateLine(i, { note2: e.target.value })}
                                   placeholder="품목 코멘트"
                                   className="w-full px-2 py-1 text-xs border border-slate-300 rounded"/>
                          </td>
                        </>
                      )}
                      <td className="px-2 py-1">
                        <div className="flex items-center justify-center gap-1">
                          <button type="button" onClick={() => insertLineAfter(i)}
                                  title="이 줄 아래에 라인 추가"
                                  className="text-emerald-600 hover:text-emerald-800">
                            <Plus className="w-4 h-4" />
                          </button>
                          <button type="button" onClick={() => removeLine(i)}
                                  title="이 줄 삭제"
                                  className="text-rose-500 hover:text-rose-700">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!edit.lines.length && (
                    <tr><td colSpan={9} className="text-center text-slate-400 py-6 text-sm">"라인 추가" 버튼을 눌러 품목을 등록하세요.</td></tr>
                  )}
                </tbody>
                <tfoot className="bg-slate-50 font-semibold">
                  <tr>
                    <td colSpan={showDateSplit ? 6 : 5} className="px-2 py-2 text-right text-xs text-slate-600">합 계</td>
                    <td className="px-2 py-2 text-right tabular-nums">{formatNum(editSubtotal)}</td>
                    <td className="px-2 py-2 text-right tabular-nums text-slate-500">{formatNum(editVat)}</td>
                    <td className={cn('px-2 py-2 text-right tabular-nums', c.totalColorClass)}>{formatNum(editTotal)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="flex items-center gap-2 pt-4">
              <div className="text-[11px] text-slate-500">
                단축키: <kbd className="px-1 py-0.5 bg-slate-100 border border-slate-300 rounded font-mono">←→↑↓</kbd> 이동 · <kbd className="px-1 py-0.5 bg-slate-100 border border-slate-300 rounded font-mono">Enter</kbd> 다음 · <kbd className="px-1 py-0.5 bg-slate-100 border border-slate-300 rounded font-mono">Del</kbd> 라인 삭제 · <kbd className="px-1 py-0.5 bg-slate-100 border border-slate-300 rounded font-mono">Ctrl+Enter</kbd> 저장
              </div>
              <div className="flex-1"/>
              <button ref={cancelBtnRef}
                      onClick={() => setEdit(null)}
                      onKeyDown={(e) => {
                        if (e.key === 'ArrowRight') { e.preventDefault(); saveBtnRef.current?.focus() }
                        else if (e.key === 'ArrowLeft') { e.preventDefault(); addLineBtnRef.current?.focus() }
                        else if (e.key === 'ArrowUp') { e.preventDefault(); focusUpFromBottomBtns() }
                      }}
                      className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg focus:ring-2 focus:ring-slate-300 focus:outline-none">취소</button>
              <button ref={saveBtnRef}
                onClick={trySave}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowLeft') { e.preventDefault(); cancelBtnRef.current?.focus() }
                  else if (e.key === 'ArrowUp') { e.preventDefault(); focusUpFromBottomBtns() }
                }}
                disabled={save.isPending}
                className="px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg focus:ring-2 focus:ring-emerald-300 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
              >
                {save.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                {save.isPending ? '저장 중…' : '저장'}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}
